import * as React from 'react';
import {
  Typography,
  TableHead,
  Box,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  TableFooter,
  Paper,
  TableContainer,
  Divider,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Slice of redux
import { fetchUserList } from 'src/store/apps/userList/UserListSlice';

// Components
import PageContainer from 'src/components/container/PageContainer';
import Breadcrumb from 'src/layouts/full/shared/breadcrumb/Breadcrumb';
import Loader from 'src/views/spinner/Loader';
import { came_fromData } from 'src/utils/constant';

const BCrumb = [
  {
    to: '/',
    title: 'Home',
  },
  {
    title: 'Users',
  },
];

const ArkchatUsersList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get URL parameters
  const queryParams = new URLSearchParams(location.search);
  
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [page, setPage] = React.useState(parseInt(queryParams.get('page')) - 1 || 0);
  const [rowsPerPage, setRowsPerPage] = React.useState(parseInt(queryParams.get('size')) || 20);
  const [selectedSource, setSelectedSource] = React.useState(queryParams.get('referredBy') || '');

  const userList = useSelector((state) => state?.userReducer?.userData?.result || []);
  const totalCount = useSelector((state) => state?.userReducer?.userData?.total || 0);

  React.useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    let needsUpdate = false;
  
    if (!currentParams.has('page')) {
      currentParams.set('page', '1');
      needsUpdate = true;
    }
    
    if (!currentParams.has('size')) {
      currentParams.set('size', '20');
      needsUpdate = true;
    }
  
    if (needsUpdate) {
      navigate({ search: currentParams.toString() }, { replace: true });
    }
  }, []);

  const updateUrlParams = (params) => {
    const urlParams = new URLSearchParams(location.search);
    
    // Update or remove referredBy
    if (params.source) {
      urlParams.set('referredBy', params.source);
    } else {
      urlParams.delete('referredBy');
    }

    // Update pagination params
    urlParams.set('page', (params.page || 0) + 1);
    urlParams.set('size', params.size || rowsPerPage);

    navigate({ search: urlParams.toString() }, { replace: true });
  };

  React.useEffect(() => {
    setIsLoading(true);
    dispatch(fetchUserList(page + 1, rowsPerPage, selectedSource)).then((res) => {
      setIsLoading(false);
    });
  }, [dispatch, page, rowsPerPage, selectedSource]);

  const filteredUsers = React.useMemo(() => {
    if (!searchTerm) return userList;
    return userList.filter((user) =>
      Object.values(user).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [userList, searchTerm]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
    updateUrlParams({ page: 0, size: rowsPerPage, source: selectedSource });
  };

  const handleSourceChange = (event) => {
    const newSource = event.target.value;
    setSelectedSource(newSource);
    setPage(0);
    updateUrlParams({ page: 0, size: rowsPerPage, source: newSource });
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    updateUrlParams({ page: newPage, size: rowsPerPage, source: selectedSource });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    updateUrlParams({ page: 0, size: newRowsPerPage, source: selectedSource });
  };

  return (
    <PageContainer title="Users" description="Here subscribers will appear">
      <Breadcrumb title="Users" items={BCrumb} />

      <Box p={2} display="flex" justifyContent="flex-end" gap={2}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Source Filter</InputLabel>
          <Select
            value={selectedSource}
            onChange={handleSourceChange}
            label="Source Filter"
          >
            <MenuItem value="">
              <em>All Sources</em>
            </MenuItem>
            {came_fromData.map((source) => (
              <MenuItem key={source.value} value={source.value}>
                {source.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Card sx={{ padding: 0 }} variant="outlined">
        <Grid container justifyContent="space-between" alignItems="center" sx={{ padding: '16px' }}>
          <Typography variant="h6" fontWeight="600">
            Users
          </Typography>
        </Grid>
        <Divider />

        <Box p={2} display="flex" justifyContent="flex-end">
          <TextField
            variant="outlined"
            placeholder="Search..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ marginLeft: 2, width: 300 }}
          />
        </Box>

        <CardContent>
          <Paper variant="outlined">
            <TableContainer>
              {isLoading && <Loader />}
              <Table aria-label="custom pagination table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="h6">ID</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">Name</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">Country</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">Language</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">Email Status</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">Referral</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Typography variant="body1">{user.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body1"
                          sx={{ cursor: 'pointer' }}
                        >
                          {`${user.firstName} ${user.lastName}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">{user.country}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">{user.language}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {user.isEmailVerified ? 'Verified' : 'Unverified'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {user.referredBy || ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50, 100]}
                      count={totalCount}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Paper>
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default ArkchatUsersList;

